<script setup>
import {onMounted, ref} from 'vue';
import DocumentService from "@/services/DocumentService";
import Button from "@/components/forms/Button.vue";
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import SignModal from '@/components/modals/SignModal.vue';
import {getLoggedUser, refreshLoggedUser} from '@/services/AuthService';
import DocumentDetailModal from '@/components/modals/DocumentDetailModal.vue';
import DownloadButtonComponent from '@/components/documents/DownloadButtonComponent.vue';
import {useI18n} from 'vue-i18n'

const {t} = useI18n()

const props = defineProps({
  entityId: {
    type: Number,
    default: null,
  },
  archive: {
    type: Boolean,
    default: false,
  },
  isNewOnboarding: {
    type: Boolean,
    default: false,
  },
});
const documents = ref([]);
const loading = ref(true);
const checkedDocuments = ref([]);
const showCheckbox = ['/my-documents', '/home'].includes(window.location.pathname) && !props.archive && !props.isNewOnboarding;
const showModal = document.location.href.includes('home');
// IMA-511: klient ve stavu Inheritance_process/Inheritance_closed/Invalid má jen náhled -
// podepisování je zápis, backend by ho zamítl (403). Stahování zůstává funkční (GET).
const isReadOnly = ref(false);
getLoggedUser().then(user => {
  isReadOnly.value = !!user?.client?.is_read_only;
});

onMounted(async () => {
      getDocuments();
    }
);

function getSignatureStateLabel(state) {
  switch (state) {
    case 'PENDING':
      return t('documentListMinimalComponent.awaiting_signature');
    case 'SIGNED':
      return t('documentListMinimalComponent.signed');
    case 'REJECTED':
      return t('documentListMinimalComponent.signature_rejected');
    default:
      return state;
  }
}

function getSignatureStateClass(state) {
  switch (state) {
    case 'PENDING':
      return 'warning';
    case 'SIGNED':
      return 'success';
    case 'REJECTED':
      return 'danger';
    default:
      return state;
  }
}

function getDocuments() {
  if (!props.entityId) {
    DocumentService.getMyDocuments()
        .then((response) => {
          loading.value = false;
          if (props.archive) {
            documents.value = response.filter((document) => document.signature_current_user?.state !== 'PENDING');
          } else {
            documents.value = response.filter((document) => document.signature_current_user?.state === 'PENDING');
            checkedDocuments.value = documents.value.map((document) => document.id);
            console.log(checkedDocuments.value);
          }

          if (document.location.href.includes('home') && documents.value.length === 0) {
            emitter.emit('noDocuments');
          }
        })
        .catch((error) => {
          console.log(error);
        });
  } else {
    DocumentService.getDocumentsForClient(props.entityId)
        .then((response) => {
          documents.value = response;
        })
        .catch((error) => {
          console.log(error);
        });
  }
}

function openSignModal() {
  const {reveal, onConfirm} = createConfirmDialog(SignModal, {documentsIds: checkedDocuments.value});
  reveal();
  onConfirm(() => {
    showSuccessMessage(t('documentListMinimalComponent.documents_successfully_signed'));
    getDocuments();
    refreshLoggedUser();
    checkedDocuments.value = [];
  });
}

function onCheck(documentId) {
  if (checkedDocuments.value.includes(documentId)) {
    checkedDocuments.value = checkedDocuments.value.filter((id) => id !== documentId);
  } else {
    checkedDocuments.value.push(documentId);
  }
}

function openDocumentDetailModal(documentId) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(DocumentDetailModal, {documentId, isNewOnboarding: props.isNewOnboarding});
  reveal();
  onConfirm(() => {
    getDocuments();
  }),
      onCancel(() => {
        getDocuments();
      });
}
</script>

<template>
  <div>
    <div v-if="loading">
      <div class="text-center">
        <div v-for="i in 5" :key="i" class="mb-2">
          <Skeletor width="100%" height="46"/>
        </div>
        <div class="row">
          <div class="col-12 d-flex justify-content-end">
            <Button v-if="!archive && !isNewOnboarding && !isReadOnly" :disabled="checkedDocuments.length === 0" label="Hromadně podepsat" @click="openSignModal"/>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="documents.length">
        <div class="simple-list">
          <div v-for="(document) in documents" :key="document" class="simple-list__item">
            <div class="row align-items-center">
              <div class="col-auto" v-if="showCheckbox">
                <input :disabled="document.signature_current_user?.state !== 'PENDING'" type="checkbox" :checked="checkedDocuments.includes(document.id)" @change="onCheck(document.id)">
              </div>
              <div class="col">
                <div class="simple-list__item__heading">{{ document.name }}</div>
                <div class="simple-list__item__info">{{ t('documentListMinimalComponent.on_date') }} {{ formatDate(document.created_at, 'd.m.Y') }}</div>
                <div>
                          <span v-if="document.signature_client" :class="'badge badge-' + getSignatureStateClass(document.signature_client?.state)">
                              {{ getSignatureStateLabel(document.signature_client?.state) }}
                          </span>
                </div>
              </div>
              <div class="col-auto">
                <router-link v-if="!showModal" :to="{ name: 'DocumentDetailSimple', params: { id: document.id} }">
                  <button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
                </router-link>
                <DownloadButtonComponent v-if="document.file" :uuid="document.file?.uuid" :downloadUrl="document.file.download_url"/>
                <button v-if="showModal" @click="openDocumentDetailModal(document.id)" type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 d-flex justify-content-end">
            <Button v-if="!archive && !isNewOnboarding && !isReadOnly" :disabled="checkedDocuments.length === 0" label="Hromadně podepsat" @click="openSignModal"/>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="simple-list">
          <div class="simple-list__empty-text">
            {{ t('documentListMinimalComponent.no_documents_yet') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
